// ai-content.ts — Grok/AI content-generation buttons for the creation forms, carved out of template.js's
// COOKIES & GENERIC UTILITIES junk-drawer (Phase 2g of plans/template-js-carve-up.md) and TS-ported for
// Phase 2a of plans/bundler-script-load-order-phase2.md — the fourth Wave-2a fold into post-template-tail.
//
// generateAIContent fills a Quill-backed text field (item/encounter description, flavor) from a Grok
// endpoint; generateObjectEconomicsFromGrok fills weight/cost_gp/daily_cost_gp (object form);
// generateCarryingCapacityFromGrok fills carrying-capacity fields (equipment form). All POST a prompt to a
// per-button endpoint URL (passed in by the PHP onclick) and write the result into the form.
//
// MODULE-SCOPE SAFE: at eval this file only defines functions + attaches its 3 window globals — no eval-time
// DOM access, no module-scope template.js reads. Its deps — ftDebugLog (template.js, bare) and getElementValue
// (core-helpers, typeof-guarded) — are reached only at button-click time → clean 2a fold.
//
// PUBLIC GLOBALS (window-attached at the bottom): generateAIContent, generateObjectEconomicsFromGrok,
// generateCarryingCapacityFromGrok — all consumed by PHP onclick in base_item/object/equipment_form_inc +
// modify_encounter. ftObjectIsCreatureLikeItemType + the _ft*InFlight guards have NO cross-file consumer
// (grep-verified; template.js's only references are extraction-note comments) and stay module-private.

async function generateAIContent(itemNameId: unknown, fieldBaseId: string, endpointUrl: string): Promise<void> {
    // === 1. Gather inputs dynamically using the fieldBaseId ===
    const fixedPromptEl   = document.getElementById(`input_improve_${fieldBaseId}`) as HTMLInputElement | null;
    const dynamicPromptEl = document.getElementById(`input_prompt_value_${fieldBaseId}`) as HTMLInputElement | null;

    if (!itemNameId || !fixedPromptEl || !dynamicPromptEl) {
        alert('Required AI prompt fields missing for this section.');
        return;
    }

    let combinedPrompt = fixedPromptEl.value.trim();

    // Prefer a visible field value over any stray duplicate id="…" hidden inputs from older markup.
    function firstFieldValueById(id: string): string {
        if (!id) return '';
        const esc = id.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
        const nodes = document.querySelectorAll('[id="' + esc + '"]');
        for (let i = 0; i < nodes.length; i++) {
            const el = nodes[i];
            if (!(el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement)) continue;
            if (el instanceof HTMLInputElement && el.type === 'hidden') continue;
            if (typeof el.value === 'string') {
                const t = el.value.trim();
                if (t !== '') return t;
            }
        }
        const fallback = document.getElementById(id);
        if ((fallback instanceof HTMLInputElement || fallback instanceof HTMLTextAreaElement || fallback instanceof HTMLSelectElement)
            && typeof fallback.value === 'string') {
            return fallback.value.trim();
        }
        return '';
    }

    const resolvedNameId = typeof itemNameId === 'string' ? itemNameId : 'item_name';
    if (typeof itemNameId !== 'string') {
        ftDebugLog('generateAIContent', { warn: 'itemNameId should be string or null; using item_name' });
    }

    const entityName = firstFieldValueById(resolvedNameId);
    if (entityName !== '') {
        combinedPrompt += '\n\nPRIMARY (authoritative name — treat this as the subject; match it exactly):\n' + entityName;
    }

    // Flavor text: use live item description as light context only; do not send stale saved flavor
    // from input_prompt_value_flavor_text (it does not track Quill edits and over-trains the model).
    let itemTypeForVoice = '';
    if (fieldBaseId === 'flavor_text') {
        const descEditor = document.querySelector('#quill_item_description .ql-editor') as HTMLElement | null;
        if (descEditor) {
            const descPlain = (descEditor.innerText || '').replace(/\s+/g, ' ').trim();
            if (descPlain !== '') {
                combinedPrompt += '\n\nOptional factual context only (secondary; do not let this override the name above or dictate tone):\n' + descPlain;
            }
        }
        // item_type rides along OUT-OF-BAND (its own JSON key, never appended to the prompt) so the
        // endpoint can pick the house-voice length rule: attack_ability / opportunity_attack_ability /
        // natural_weapon run terser than spells per the locked standard. Keeping it out of the prompt
        // text matters — the standard treats theme signals as PRIVATE, to be embodied, never restated.
        if (typeof getElementValue === 'function') {
            const v = getElementValue('item_type');
            itemTypeForVoice = Array.isArray(v) ? String(v[0] || '').trim() : String(v || '').trim();
        } else {
            const el = document.getElementById('item_type');
            itemTypeForVoice = el instanceof HTMLInputElement || el instanceof HTMLSelectElement
                ? String(el.value || '').trim()
                : '';
        }
    } else {
        const dynamic = dynamicPromptEl.value.trim();
        if (dynamic !== '') {
            combinedPrompt += '\n\n' + dynamic;
        }
    }

    // === 2. Find UI elements for loading state and output ===
    const generateBtn = document.getElementById(`generate-ai-btn-${fieldBaseId}`) as HTMLButtonElement | null;
    const quillEditor = document.querySelector(`#quill_${fieldBaseId} .ql-editor`) as HTMLElement | null;
    const hiddenTextarea = document.getElementById(fieldBaseId) as HTMLInputElement | HTMLTextAreaElement | null;

    if (!generateBtn || !quillEditor || !hiddenTextarea) {
        alert('AI generate button or editor not found.');
        return;
    }

    // Save original button HTML for restoration
    const originalBtnHTML = generateBtn.innerHTML;

    // Loading state
    generateBtn.disabled = true;
    generateBtn.innerHTML = `<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>`;

    try {
        const response = await fetch(endpointUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ prompt: combinedPrompt, item_type: itemTypeForVoice })
        });

        if (!response.ok) {
            throw new Error(`Network error: ${response.status}`);
        }

        const data = await response.json();

        if (data.error) {
            throw new Error(data.error);
        }

        const generatedText = data.text.trim();

        if (!generatedText) {
            throw new Error('Empty response from AI');
        }

        // === 3. Insert into Quill editor (replace existing content) ===
        // Converts line breaks to paragraphs for nice formatting
        quillEditor.innerHTML = '<p>' + generatedText.replace(/\n/g, '</p><p>') + '</p>';

        // === 4. Sync to hidden textarea for form submission ===
        hiddenTextarea.value = quillEditor.innerHTML;

        ftDebugLog('generateAIContent', { fieldBaseId, generatedLen: generatedText?.length });

        // Optional: trigger any custom callback if the Quill container has one
        const quillContainer = document.getElementById(`quill_${fieldBaseId}`);
        if (quillContainer && quillContainer.dataset.updateCallback) {
            const callbackName = quillContainer.dataset.updateCallback;
            const callback = (window as unknown as Record<string, unknown>)[callbackName];
            if (typeof callback === 'function') {
                callback(quillContainer);
            }
        }

    } catch (err) {
        console.error(`AI generation failed for ${fieldBaseId}:`, err);
        alert('Failed to generate content. Check console or server logs for details.');
    } finally {
        // Restore button
        generateBtn.disabled = false;
        generateBtn.innerHTML = originalBtnHTML;
    }
}

// OBJECT form: Grok fills one field per click — weight, cost_gp, or daily_cost_gp (base_object_form_inc).
let _ftObjectEconomicsAiInFlight: Record<string, boolean> = {};

function ftObjectIsCreatureLikeItemType(): boolean {
    let v: unknown = '';
    if (typeof getElementValue === 'function') {
        v = getElementValue('item_type');
        v = Array.isArray(v) ? (v[0] || '') : v;
    } else {
        const el = document.getElementById('item_type');
        v = el instanceof HTMLInputElement || el instanceof HTMLSelectElement ? String(el.value || '') : '';
    }
    v = String(v || '').trim();
    // creature_sub_type item_type FAMILY (2026-07-15 three-division naming + legacy slugs) —
    // inlined; this file is a deliberately import-free 2a fold (see header), so it can't pull
    // the shared helper. Keep in lockstep with shared/item-type-badge.ts CREATURE_SUB_TYPE_ITEM_TYPES.
    const subTypeFamily = ['creature_ancestry', 'creature_grouping', 'creature_lineage',
        'creature_sub_type', 'creature_sub_type_ancestral', 'creature_sub_type_grouping'];
    return v === 'creature' || v === 'creature_template' || subTypeFamily.indexOf(v as string) !== -1;
}

async function generateObjectEconomicsFromGrok(endpointUrl: string, triggerBtn: HTMLButtonElement | null, targetField: string): Promise<void> {
    if (!endpointUrl) {
        alert('AI endpoint not configured.');
        return;
    }
    const allowed = ['weight', 'cost_gp', 'daily_cost_gp'];
    if (allowed.indexOf(targetField) === -1) {
        return;
    }

    if (!ftObjectIsCreatureLikeItemType() && targetField === 'daily_cost_gp') {
        const dEl = document.getElementById('daily_cost_gp') as HTMLInputElement | null;
        if (dEl) {
            dEl.value = '0';
        }
        return;
    }

    if (_ftObjectEconomicsAiInFlight[targetField]) {
        return;
    }

    function quillPlain(layerKey: string): string {
        const ed = document.querySelector('#quill_' + layerKey + ' .ql-editor') as HTMLElement | null;
        if (!ed) {
            return '';
        }
        return (ed.innerText || '').replace(/\s+/g, ' ').trim();
    }

    const name = ((document.getElementById('item_name') as HTMLInputElement | null)?.value || '').trim();
    let itemType = '';
    if (typeof getElementValue === 'function') {
        const v = getElementValue('item_type');
        itemType = Array.isArray(v) ? v.filter(Boolean).join(', ') : String(v || '').trim();
    } else {
        const el = document.getElementById('item_type') as HTMLInputElement | null;
        itemType = el ? String(el.value || '').trim() : '';
    }
    const desc = quillPlain('item_description');
    const flavor = quillPlain('flavor_text');
    const isCreatureLike = ftObjectIsCreatureLikeItemType();

    const fieldInstructions: Record<string, string> = {
        weight: 'Estimate WEIGHT only (pounds). Ignore cost and hire rate.',
        cost_gp: 'Estimate PURCHASE / OWNERSHIP price in gp only (buyout; not per-day hire). For creatures, this is capital to acquire them, not daily contract pay.',
        daily_cost_gp: 'Estimate DAILY HIRE / contract / rental rate in gp for one day only (hirer pays; short-term hire is usually dearer per day than amortized ownership).',
    };

    let prompt = fieldInstructions[targetField] + '\n';
    prompt += 'IS_CREATURE_LIKE_ITEM_TYPE: ' + (isCreatureLike ? 'yes' : 'no') + '\n';
    prompt += 'Respond with JSON for this single field only (server enforces which key).';
    if (name !== '') {
        prompt += '\n\nPRIMARY (authoritative item name):\n' + name;
    }
    if (itemType !== '') {
        prompt += '\n\nItem type / category:\n' + itemType;
    }
    if (desc !== '') {
        prompt += '\n\nDescription:\n' + desc;
    }
    if (flavor !== '') {
        prompt += '\n\nFlavor:\n' + flavor;
    }

    const originalHtml = triggerBtn ? triggerBtn.innerHTML : '';
    const originalDisabled = triggerBtn ? triggerBtn.disabled : false;
    _ftObjectEconomicsAiInFlight[targetField] = true;
    if (triggerBtn) {
        triggerBtn.disabled = true;
        triggerBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';
    }

    try {
        const response = await fetch(endpointUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ prompt: prompt, target: targetField }),
        });
        if (!response.ok) {
            throw new Error('Network error: ' + response.status);
        }
        const data = await response.json();
        if (data.error) {
            throw new Error(data.error);
        }

        const wEl = document.getElementById('weight') as HTMLInputElement | null;
        const cEl = document.getElementById('cost_gp') as HTMLInputElement | null;
        const dEl = document.getElementById('daily_cost_gp') as HTMLInputElement | null;
        if (targetField === 'weight' && data.weight != null && wEl) {
            wEl.value = String(data.weight);
        }
        if (targetField === 'cost_gp' && data.cost_gp != null && cEl) {
            cEl.value = String(data.cost_gp);
        }
        if (targetField === 'daily_cost_gp' && data.daily_cost_gp != null && dEl) {
            dEl.value = String(data.daily_cost_gp);
        }
    } catch (err) {
        console.error('Object economics AI failed:', err);
        alert('Failed to fill this field from AI. Check console or server logs for details.');
    } finally {
        delete _ftObjectEconomicsAiInFlight[targetField];
        if (triggerBtn) {
            triggerBtn.disabled = originalDisabled;
            triggerBtn.innerHTML = originalHtml;
        }
    }
}


// EQUIPMENT form: Grok fills one carrying-capacity field per click — pounds, cu_ft, cu_in, or people (base_equipment_form_inc).
let _ftCarryingCapacityAiInFlight: Record<string, boolean> = {};

async function generateCarryingCapacityFromGrok(endpointUrl: string, triggerBtn: HTMLButtonElement | null, targetField: string): Promise<void> {
    if (!endpointUrl) {
        alert('AI endpoint not configured.');
        return;
    }
    const allowed = [
        'carrying_capacity_pounds',
        'carrying_capacity_cu_ft',
        'carrying_capacity_cu_in',
        'carrying_capacity_people',
    ];
    if (allowed.indexOf(targetField) === -1) {
        return;
    }

    if (_ftCarryingCapacityAiInFlight[targetField]) {
        return;
    }

    function quillPlain(layerKey: string): string {
        const ed = document.querySelector('#quill_' + layerKey + ' .ql-editor') as HTMLElement | null;
        if (!ed) {
            return '';
        }
        return (ed.innerText || '').replace(/\s+/g, ' ').trim();
    }

    const name = ((document.getElementById('item_name') as HTMLInputElement | null)?.value || '').trim();
    let itemType = '';
    if (typeof getElementValue === 'function') {
        const v = getElementValue('item_type');
        itemType = Array.isArray(v) ? v.filter(Boolean).join(', ') : String(v || '').trim();
    } else {
        const el = document.getElementById('item_type') as HTMLInputElement | null;
        itemType = el ? String(el.value || '').trim() : '';
    }
    const desc = quillPlain('item_description');
    const flavor = quillPlain('flavor_text');

    const fieldInstructions: Record<string, string> = {
        carrying_capacity_pounds: 'Estimate the maximum payload WEIGHT (in pounds) this subject can hold/carry/transport. This is capacity, NOT the subject\'s own weight.',
        carrying_capacity_cu_ft: 'Estimate the usable interior VOLUME in cubic feet that this subject can hold.',
        carrying_capacity_cu_in: 'Estimate the usable interior VOLUME in cubic INCHES that this subject can hold (use for small containers — pouches, vials, lockets).',
        carrying_capacity_people: 'Estimate how many adult-human-sized OCCUPANTS this subject can seat / fit / transport. Whole number.',
    };

    let prompt = fieldInstructions[targetField] + '\n';
    prompt += 'Respond with JSON for this single field only (server enforces which key).';
    if (name !== '') {
        prompt += '\n\nPRIMARY (authoritative item name):\n' + name;
    }
    if (itemType !== '') {
        prompt += '\n\nItem type / category:\n' + itemType;
    }
    if (desc !== '') {
        prompt += '\n\nDescription:\n' + desc;
    }
    if (flavor !== '') {
        prompt += '\n\nFlavor:\n' + flavor;
    }

    const originalHtml = triggerBtn ? triggerBtn.innerHTML : '';
    const originalDisabled = triggerBtn ? triggerBtn.disabled : false;
    _ftCarryingCapacityAiInFlight[targetField] = true;
    if (triggerBtn) {
        triggerBtn.disabled = true;
        triggerBtn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>';
    }

    try {
        const response = await fetch(endpointUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ prompt: prompt, target: targetField }),
        });
        if (!response.ok) {
            throw new Error('Network error: ' + response.status);
        }
        const data = await response.json();
        if (data.error) {
            throw new Error(data.error);
        }

        const fieldEl = document.getElementById(targetField) as HTMLInputElement | null;
        if (fieldEl && data[targetField] != null) {
            fieldEl.value = String(data[targetField]);
        }
    } catch (err) {
        console.error('Carrying capacity AI failed:', err);
        alert('Failed to fill this field from AI. Check console or server logs for details.');
    } finally {
        delete _ftCarryingCapacityAiInFlight[targetField];
        if (triggerBtn) {
            triggerBtn.disabled = originalDisabled;
            triggerBtn.innerHTML = originalHtml;
        }
    }
}


// ===========================================================================================
// Window attachment — preserve the public surface the original exposed (3 globals, all PHP-onclick callers).
// ===========================================================================================
declare global {
    interface Window {
        generateAIContent: typeof generateAIContent;
        generateObjectEconomicsFromGrok: typeof generateObjectEconomicsFromGrok;
        generateCarryingCapacityFromGrok: typeof generateCarryingCapacityFromGrok;
    }
}

window.generateAIContent = generateAIContent;
window.generateObjectEconomicsFromGrok = generateObjectEconomicsFromGrok;
window.generateCarryingCapacityFromGrok = generateCarryingCapacityFromGrok;

export {};
